import {Container} from 'pixi.js';
import {ITrackEntry, IEvent} from 'pixi-spine';
import {
    AnimationProperty,
    Button,
    CoreDI,
    CurrencyFormatter, Easing,
    inject,
    LanguageService, Meter,
    RichText, ScreenParameters,
    Spine,
    UpdateManager
} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {
    GenericPopupConfig,
    GenericPopupCounterValue,
    GenericPopupData,
    IReelsScaleService, PopupSpineConfig,
    GameConfig
} from '@gongxigames/framework/slot/types';
import {GenericPopupModuleEvents} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupModule';
import {FaderColor, FaderImage, FaderSpine, Fader, DEFAULT_FADE_DURATION} from '@gongxigames/framework/slot/modules/genericPopup/fader';
import {View} from '@gongxigames/framework/slot';

export class GenericPopupView extends View<GenericPopupModuleEvents> {
    public static readonly NAME: symbol = Symbol('PopupView');
    public static layer = LAYERS.POPUPS;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    protected _continueButton?: Button;
    protected _spineContainer!: Container;
    protected _spineUpdateManager!: UpdateManager;

    protected _spines: Spine[] = [];
    protected _fader?: Fader;
    protected _config!: GenericPopupConfig;
    protected data?: GenericPopupData;

    protected _counter?: RichText;
    protected _meter?: Meter;

    protected _faders = [FaderColor, FaderImage, FaderSpine];

    public init(): void {
        super.init();

        this._spines = [];
        this._spineContainer = new Container();
        this._spineUpdateManager = new UpdateManager();

        this._spineUpdateManager.start();
        this.updateManager.add(this._spineUpdateManager);
        this.container.addChild(this._spineContainer);
    }

    public show(): void {
        super.show();

        this.showSpines();
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this._spineContainer);
    }

    public onContinueButtonClicked(): void {
        this.disableContinueButton();
        this.playContinueButtonSound();
        this.emit('GenericPopupModuleEvents.CONTINUE_BUTTON_CLICKED', this._config.type);
    }

    public disableContinueButton(): void {
        if (this._continueButton && this._continueButton.enabled) {
            this._continueButton.disable();
        }
    }

    public hide(): void {
        super.hide();

        this._spines.forEach(spine => this._spineUpdateManager.remove(spine));
        this._spineContainer.removeChildren();
        this._spines.length = 0;
        this._fader?.hide();
        this._fader = undefined;
        this._continueButton = undefined;

        this.emit('GenericPopupModuleEvents.CLOSED', this.getPopupEmitType());
    }

    public initPopup(config: GenericPopupConfig, data?: GenericPopupData): void {
        const {specialData} = data || {};

        this.config = config;
        this.data = data;
        this.initItemsFromConfig();

        if (specialData) {
            this.applyPopupSpecialData(data);
        }

        this.forceUpdateOrientation();
    }

    public initItemsFromConfig(): void {
        this.initFader();
        this.initSpines();
    }

    public playFaderTween(): void {
        if (this._fader) {
            this.container.visible = true;
            this._fader.show(this.onFadeInAnimationFinished.bind(this));
        } else {
            this.onFadeInAnimationFinished();
        }
    }

    /**
     * For override
     */
    protected applyPopupSpecialData(data?: GenericPopupData): void {
    }

    protected initMeter(value: GenericPopupCounterValue): void {
        if (this._counter) {
            this._meter = new Meter(this._counter, Easing.Linear.None, this._currencyFormatter);
            this._meter.start();
            this._meter.from(value.from || 0).to(value.to, value.duration || 0);

            this.updateManager.add(this._meter);
        }
    }

    public playFadeOutAnimation(): void {
        const fadeDuration = this._config.fader?.fadeDuration || DEFAULT_FADE_DURATION;

        this.updateManager.addTween(this.container)
            .to({alpha: 0.0}, fadeDuration)
            .onComplete(this.onFadeOutAnimationFinished.bind(this))
            .start();

        this.emit('GenericPopupModuleEvents.FADE_OUT_STARTED', this.getPopupEmitType());
    }

    public showSpines(): void {
        this._spines.forEach(spine => spine.visible = true);
    }

    public playShowAnimation(): void {
        const promises = this._config.spine.map(spineConfig => this.playStartAnimation(spineConfig));

        Promise.all(promises).then(this.showAnimationFinished.bind(this));
    }

    protected playStartAnimation(spineConfig: PopupSpineConfig): Promise<any> | undefined {
        const {name} = spineConfig.spine;
        const {start, skin} = spineConfig.animation!;

        if (start && typeof start === 'string') {
            const getAnimationProperty = (resolve: (value: any) => void) => {
                const completeCallBack = this.getStartAnimationCompleteCallBack(resolve);
                const eventCallBack = this.getStartAnimationEventCallBack(spineConfig);

                return this.getStartAnimationProperty(start, skin, completeCallBack, eventCallBack);
            };

            return new Promise(resolve => this.playAnimationInSpine(name, getAnimationProperty(resolve)));
        }
    }

    /**
     * Can be overridden
     */
    protected getStartAnimationCompleteCallBack(resolve: (value: any) => void): (value: any) => void {
        return resolve;
    }

    protected getStartAnimationEventCallBack(spineConfig: PopupSpineConfig): (entry: ITrackEntry, event: IEvent) => void {
        return this.applyButtonEnableEvent(spineConfig);
    }

    protected applyButtonEnableEvent(spineConfig: PopupSpineConfig): (entry: ITrackEntry, event: IEvent) => void {
        const enableEvent = spineConfig.continueButton?.enableEvent;

        return (entry: ITrackEntry, event: IEvent) => {
            if (enableEvent && event.data.name === enableEvent) {
                this._continueButton?.enable();
            }
        };
    }

    protected getStartAnimationProperty(
        animationName: string,
        skinName: string | undefined,
        complete: (entry: ITrackEntry) => void,
        event: (entry: ITrackEntry, event: IEvent) => void = () => {
        }
    ): AnimationProperty {
        return {animationName, listeners: {complete, event}, skinName};
    }

    public playWaitingSound(): void {
        const {waiting} = this.config.soundConfig;

        this.playSound(waiting);
    }

    public playTransitionEnterSound(): void {
        const {transitionEnter} = this.config.soundConfig;

        this.playSound(transitionEnter);
    }

    public playExitSound(): void {
        const {exit} = this.config.soundConfig;

        this.playSound(exit);
    }

    public playContinueButtonSound(): void {
        const {buttonClickSound} = this.config.soundConfig;

        this.playSound(buttonClickSound);
    }

    protected playSound(sound?: string): void {
        if (sound) {
            this.soundPlayer.play(sound);
        }
    }

    protected initSpines(): void {
        this._config.spine.forEach(spineConfig => this.initSpine(spineConfig));
    }

    protected initSpine(spineConfig: PopupSpineConfig): void {
        const spine = this._objectBuilder.buildSpineWithConfig(spineConfig.spine);

        if (spine) {
            spine.name = spineConfig.spine.name;
            this.setPopupSpine(spine);
            this.setChangesByType(spineConfig, spine);
        }
    }

    protected setPopupSpine(spine: Spine): void {
        spine.x = this._gameConfig.mainFrame.width / 2;
        spine.y = this._gameConfig.mainFrame.height / 2;
        spine.visible = false;

        spine.start();

        this._spineUpdateManager.add(spine);
        this._spineContainer.addChild(spine);
        this._spineUtils.applyMasks(spine);
        this._spines.push(spine);
    }

    protected setContinueButton(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {continueButton} = spineConfig;

        if (continueButton) {
            this._continueButton = this._objectBuilder.setUpSlot(continueButton, spine) as Button;
            this._continueButton.callback = this.onContinueButtonClicked.bind(this);
            this._continueButton.disable();
        }
    }

    protected setPopupValue(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {value} = spineConfig;

        if (value) {
            this._objectBuilder.setUpSlot(value, spine);
        }
    }

    protected setPopupCounter(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {counter} = spineConfig;

        if (counter) {
            this._counter = this._objectBuilder.setUpSlot(counter, spine) as RichText;
        }
    }

    /**
     * For override
     */
    protected setChangesByType(spineConfig: PopupSpineConfig, spine: Spine): void {
    }

    protected initFader(): void {
        const {fader} = this._config;

        if (!fader) {
            return;
        }

        const constructor = this._faders.find(constructor => constructor.testSettingsType(fader))!;

        this._fader = new constructor(
            this.updateManager,
            this.objectBuilder,
            this._displayObjectUtils,
            this.spineUtils,
            fader
        );

        this.container.addChildAt(this._fader.container, 0);
    }

    public onResized(screenParams: ScreenParameters): void {
        this._fader?.resize(screenParams);
    }

    protected onFadeOutAnimationFinished(): void {
        this.hide();
        this.container.alpha = 1;
    }

    protected onFadeInAnimationFinished(): void {
        this.show();
        this.playShowAnimation();
        this.setSpinesSlots();
        this.emit('GenericPopupModuleEvents.FADE_IN_FINISHED', this.getPopupEmitType());
    }

    protected setSpinesSlots(): void {
        const checkOldSpineStructure = (spineConfig: PopupSpineConfig, value: string) => {
            if (spineConfig.value) {
                const {text, richText} = spineConfig.value;

                if (richText) {
                    richText.values = {value};
                } else if (text) {
                    text.text = value;
                }
            }
        };

        this._config.spine.forEach(spineConfig => {
            const spine = this.getSpineByName(spineConfig.spine.name);

            if (spine) {
                if (this.data?.counterValue) {
                    checkOldSpineStructure(spineConfig, this.data.counterValue.to.toString());
                }
                this.setContinueButton(spineConfig, spine);
                this.setPopupValue(spineConfig, spine);
                this.setPopupCounter(spineConfig, spine);

                if (this.data?.counterValue) {
                    this.initMeter(this.data.counterValue);
                }
            }
        });
    }

    protected showAnimationFinished(): void {
        if (this._continueButton && !this._continueButton.enabled) {
            this._continueButton.enable();
        }

        this.playIdleAnimation();
    }

    protected playIdleAnimation(): void {
        this._config.spine.forEach(spineConfig => {
            const {idle} = spineConfig.animation!;
            const {name} = spineConfig.spine;

            if (idle && typeof idle === 'string') {
                this.playAnimationInSpine(name, this.getIdleAnimationProperty(idle));
            }
        });
    }

    protected getIdleAnimationProperty(animationName: string): AnimationProperty {
        return {animationName, loop: true};
    }

    protected playAnimationInSpine(spineName: string, animationParam: AnimationProperty): void {
        this._spines.forEach(spine => {
            if (spine.name === spineName) {
                spine.setAnimation(animationParam);
            }
        });
    }

    public getPopupEmitType(): symbol {
        const {type, emitType} = this._config;

        return emitType || type;
    }

    protected getSpineByName(spineName: string): Spine | undefined {
        return this._spines.find(spine => spine.name === spineName);
    }

    public set config(value: GenericPopupConfig) {
        this._config = value;
    }

    public get config(): GenericPopupConfig {
        return this._config;
    }

    public get continueButton(): Button {
        return this._continueButton!;
    }
}
